import { useState } from 'react'
import { useLocation, Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  IBM_VERIFY_AUTHORIZE_URL,
  IBM_VERIFY_CLIENT_ID,
  SSO_REDIRECT_URI,
} from '@/config/env'
import {
  generateCodeVerifier,
  generateCodeChallenge,
  generateState,
} from '@/utils/pkce'

export default function Login() {
  const location = useLocation()
  const [isPending, setIsPending] = useState(false)
  const ssoError = (location.state as { ssoError?: string } | null)?.ssoError

  const handleSSOLogin = async () => {
    setIsPending(true)

    const codeVerifier = generateCodeVerifier()
    const codeChallenge = await generateCodeChallenge(codeVerifier)
    const state = generateState()

    sessionStorage.setItem('sso_code_verifier', codeVerifier)
    sessionStorage.setItem('sso_state', state)

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: IBM_VERIFY_CLIENT_ID,
      redirect_uri: SSO_REDIRECT_URI,
      scope: 'openid',
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    })

    window.location.href = `${IBM_VERIFY_AUTHORIZE_URL}?${params.toString()}`
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-border shadow-sm">
        <CardContent className="flex flex-col items-center gap-8 p-16">
          {/* Logo Section */}
          <div className="flex flex-col items-center gap-2.5">
            <img src="/logo.svg" alt="Michaels" />
          </div>

          {/* Heading */}
          <h1 className="text-2xl font-medium text-primary">Sign In to CAT</h1>

          {/* SSO Button */}
          <Button
            onClick={handleSSOLogin}
            disabled={isPending}
            className="w-full h-10"
          >
            {isPending ? (
              <>
                <Loader2 className="animate-spin" />
                Signing in...
              </>
            ) : (
              'Sign in with SSO'
            )}
          </Button>

          {/* Error Message */}
          {ssoError && <p className="text-sm text-destructive">{ssoError}</p>}

          {/* Help Text */}
          <p className="text-xs text-foreground">
            Don't have an account? Contact your admin for access.
          </p>

          <Link
            to="/cred-login"
            className="text-xs text-muted-foreground hover:text-primary cursor-pointer"
          >
            Sign in with credentials
          </Link>
        </CardContent>
      </Card>
    </div>
  )
}
